import { getDb, RadarSession } from './db';

const ACTIVE_SESSION_STORAGE_KEY = 'noderadar_active_session_id';

/**
 * Manages sessions lifecycle, active session selection, and historical session deletion.
 */
export class SessionManager {
  private activeSession: RadarSession | null = null;
  private changeHandlers: ((session: RadarSession) => void)[] = [];

  /**
   * Loads the current active session or generates a default one if none exists.
   *
   * @returns Active RadarSession
   */
  async init(): Promise<RadarSession> {
    const db = await getDb();
    const savedId = localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);

    if (savedId) {
      const existing = await db.get('sessions', savedId);
      if (existing) {
        this.activeSession = existing;
        return existing;
      }
    }

    // Check if there are any sessions in the DB
    const all = await db.getAll('sessions');
    if (all.length > 0) {
      // Pick latest session
      all.sort((a, b) => b.startTime - a.startTime);
      this.activeSession = all[0];
      localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, this.activeSession.id);
      return this.activeSession;
    }

    // Create fresh initial session
    return this.createNewSession();
  }

  /**
   * Creates and persists a new session and sets it as active.
   *
   * @param customName - Optional user-defined name for the session
   * @returns Newly created session
   */
  async createNewSession(customName?: string): Promise<RadarSession> {
    const db = await getDb();
    const now = Date.now();
    const dateStr = new Date(now).toLocaleString();
    const id = `session_${now}_${Math.random().toString(36).substring(2, 7)}`;

    const newSession: RadarSession = {
      id,
      name: customName || `Session ${dateStr}`,
      startTime: now,
      endTime: null,
      packetCount: 0,
      active: true
    };

    await db.put('sessions', newSession);
    this.activeSession = newSession;
    localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, id);

    this.notifyChange(newSession);
    return newSession;
  }

  /**
   * Switches the active session to a previously saved session.
   *
   * @param sessionId - Session identifier
   */
  async switchSession(sessionId: string): Promise<RadarSession | null> {
    const db = await getDb();
    const session = await db.get('sessions', sessionId);
    if (session) {
      this.activeSession = session;
      localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, session.id);
      this.notifyChange(session);
      return session;
    }
    return null;
  }

  /**
   * Retrieves all historical sessions sorted by start time descending.
   */
  async getAllSessions(): Promise<RadarSession[]> {
    const db = await getDb();
    const all = await db.getAll('sessions');
    return all.sort((a, b) => b.startTime - a.startTime);
  }

  /**
   * Increments the packet counter for the active session.
   */
  async incrementPacketCount(): Promise<void> {
    if (!this.activeSession) return;
    this.activeSession.packetCount++;
    const db = await getDb();
    await db.put('sessions', this.activeSession);
  }

  /**
   * Deletes a session and all packets and traces linked to it.
   *
   * @param sessionId - Target session identifier
   */
  async deleteSession(sessionId: string): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(['sessions', 'packets', 'traces'], 'readwrite');

    await tx.objectStore('sessions').delete(sessionId);

    // Remove packets linked to this session
    const packetIndex = tx.objectStore('packets').index('by-session');
    let packetCursor = await packetIndex.openCursor(IDBKeyRange.only(sessionId));
    while (packetCursor) {
      await packetCursor.delete();
      packetCursor = await packetCursor.continue();
    }

    // Remove traces linked to this session
    const traceIndex = tx.objectStore('traces').index('by-session');
    let traceCursor = await traceIndex.openCursor(IDBKeyRange.only(sessionId));
    while (traceCursor) {
      await traceCursor.delete();
      traceCursor = await traceCursor.continue();
    }

    await tx.done;

    if (this.activeSession?.id === sessionId) {
      await this.init();
    }
  }

  /**
   * Clears all stored packets and traces for the active session without deleting the session itself.
   *
   * Why: Allows users to reset erroneous tracking points while preserving session identity and metadata.
   */
  async clearCurrentSessionPackets(): Promise<void> {
    if (!this.activeSession) return;
    const sessionId = this.activeSession.id;
    const db = await getDb();
    const tx = db.transaction(['sessions', 'packets', 'traces'], 'readwrite');

    const packetIndex = tx.objectStore('packets').index('by-session');
    let packetCursor = await packetIndex.openCursor(IDBKeyRange.only(sessionId));
    while (packetCursor) {
      await packetCursor.delete();
      packetCursor = await packetCursor.continue();
    }

    const traceIndex = tx.objectStore('traces').index('by-session');
    let traceCursor = await traceIndex.openCursor(IDBKeyRange.only(sessionId));
    while (traceCursor) {
      await traceCursor.delete();
      traceCursor = await traceCursor.continue();
    }

    this.activeSession.packetCount = 0;
    await tx.objectStore('sessions').put(this.activeSession);
    await tx.done;

    this.notifyChange(this.activeSession);
  }

  getActiveSession(): RadarSession | null {
    return this.activeSession;
  }

  onSessionChange(handler: (session: RadarSession) => void): void {
    this.changeHandlers.push(handler);
  }

  private notifyChange(session: RadarSession): void {
    for (const h of this.changeHandlers) {
      try {
        h(session);
      } catch (err) {
        console.error('[SessionManager] Change handler error:', err);
      }
    }
  }
}
